import { useEffect, useEffectEvent, type ReactNode } from 'react';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { CloseIcon } from '@utils/icons';
import { useReducedMotion } from '@hooks/useReducedMotion';
import styles from './Drawer.module.css';

interface IDrawerCompanionProps {
  /** Drives the enter/exit animation. When it flips to false the panel plays
   *  its close animation, then calls `onExited` so the parent can unmount it
   *  (the issue drawer's mounted/open two-state idiom). */
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  ariaLabel: string;
  closeLabel: string;
  children: ReactNode;
}

/**
 * A second panel docked immediately LEFT of a `Drawer` — the issue drawer's
 * "set locations" idiom, as a reusable slot (`Drawer`'s `companion` prop).
 * Its own scroll, so the drawer's content is never displaced; Escape closes
 * it FIRST (innermost surface) and only a second Escape reaches the drawer.
 */
export function DrawerCompanion({
  open,
  onClose,
  onExited,
  ariaLabel,
  closeLabel,
  children,
}: IDrawerCompanionProps) {
  const prefersReducedMotion = useReducedMotion();

  // Read the latest callbacks without making them effect deps (fresh
  // closures each parent render) — the codebase's useEffectEvent idiom.
  const notifyExited = useEffectEvent(() => onExited?.());
  // Reduced motion runs no exit animation, so there is no animationend to
  // unmount on — fire the exit notification straight away.
  useEffect(() => {
    if (!open && prefersReducedMotion) notifyExited();
  }, [open, prefersReducedMotion]);

  // Escape closes the companion first. Captured at the document so it runs
  // ahead of Base UI's own dismissal and the drawer stays open beneath.
  const onEscape = useEffectEvent(() => onClose());
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onEscape();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  return (
    <aside
      className={`${styles.companion} ${open ? styles.companionOpen : styles.companionClosing}`}
      aria-label={ariaLabel}
      onAnimationEnd={(e) => {
        if (!open && e.target === e.currentTarget) onExited?.();
      }}
    >
      <button type="button" className={styles.companionClose} onClick={onClose} aria-label={closeLabel}>
        <CloseIcon size={18} />
      </button>
      <ScrollArea className={styles.companionScroll} viewportClassName={styles.companionBody}>
        {children}
      </ScrollArea>
    </aside>
  );
}
